/* ==========================================================================
   MYHOLDAL — Home page redesign (2026)
   1. Scroll-reveal observer
   2. Offers switcher (auto-advancing tablist)
   3. New-arrivals carousel + collection tabs
   4. Add-to-bag (through the theme cart drawer) and quick view
   5. Top-banner slideshow
   Loaded once, re-runs on Shopify theme-editor events.
   ========================================================================== */
(function () {
  if (window.__mhHomeMotion) return;
  window.__mhHomeMotion = true;

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- reveal */

  function show(el) {
    el.classList.add('is-visible');
  }

  var observer =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (!entry.isIntersecting) return;
              show(entry.target);
              observer.unobserve(entry.target);
            });
          },
          { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
        )
      : null;

  // Safety net: on a tall viewport (or a short page) an element can sit inside
  // the viewport yet never cross the observer's negative bottom margin, and with
  // no scroll to follow it would stay at opacity 0 forever. Reveal anything that
  // is genuinely on screen.
  function sweep() {
    var pending = document.querySelectorAll('[data-mh-reveal]:not(.is-visible)');

    Array.prototype.forEach.call(pending, function (el) {
      var rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) {
        show(el);
        if (observer) observer.unobserve(el);
      }
    });
  }

  var sweepTimer = null;
  function sweepSoon() {
    clearTimeout(sweepTimer);
    sweepTimer = setTimeout(sweep, 150);
  }

  // The observer and the scroll sweep both depend on something happening. If
  // the observer's callback never fires - it has been seen not to in an
  // embedded preview - and the reader has not scrolled since the section came
  // into view, cards stay at opacity 0 with nothing left to trigger them. A
  // bounded heartbeat closes that gap: it checks a few times a second for the
  // first twelve seconds and stops as soon as nothing is left pending, so it
  // costs nothing on a page that is already behaving.
  var beat = null;
  var beatsLeft = 30;
  function startHeartbeat() {
    if (beat) return;
    beat = setInterval(function () {
      sweep();
      beatsLeft -= 1;
      if (beatsLeft <= 0 || !document.querySelector('[data-mh-reveal]:not(.is-visible)')) {
        clearInterval(beat);
        beat = null;
      }
    }, 400);
  }

  window.addEventListener('load', sweepSoon);
  window.addEventListener('resize', sweepSoon);
  // Scrolling fast can outrun the observer's callback; a debounced sweep on
  // scroll idle guarantees anything actually on screen ends up revealed.
  window.addEventListener('scroll', sweepSoon, { passive: true });

  function initReveal(scope) {
    var nodes = scope.querySelectorAll('[data-mh-reveal]:not(.is-visible)');

    Array.prototype.forEach.call(nodes, function (el) {
      // Stagger against SIBLINGS, not the whole document - a global index gave
      // cards near the bottom of a long page delays of several seconds.
      if (!el.style.getPropertyValue('--mh-delay')) {
        var step = parseInt(el.getAttribute('data-mh-stagger') || '0', 10);
        if (step && el.parentNode) {
          var row = Array.prototype.filter.call(el.parentNode.children, function (c) {
            return c.hasAttribute && c.hasAttribute('data-mh-reveal');
          });
          var index = row.indexOf(el);
          el.style.setProperty('--mh-delay', step * (index < 0 ? 0 : index) + 'ms');
        }
      }

      if (reduced || !observer) {
        show(el);
      } else {
        observer.observe(el);
      }
    });

    setTimeout(sweep, 1200);
    beatsLeft = 30;
    startHeartbeat();
  }

  /* ---------------------------------------------------------------- offers */

  function initOffers(scope) {
    var roots = scope.querySelectorAll('[data-mh-offers]:not([data-mh-ready])');

    Array.prototype.forEach.call(roots, function (root) {
      root.setAttribute('data-mh-ready', 'true');

      var tabs = Array.prototype.slice.call(root.querySelectorAll('.mh-offers__tab'));
      var panels = Array.prototype.slice.call(root.querySelectorAll('.mh-offers__panel'));
      if (tabs.length < 2) return;

      var autoplay = root.getAttribute('data-autoplay') === 'true' && !reduced;
      var interval = parseInt(root.getAttribute('data-interval') || '6000', 10);
      var current = 0;
      var timer = null;
      var inView = true;
      var held = false;

      function paint(index) {
        tabs.forEach(function (tab, i) {
          var on = i === index;
          tab.setAttribute('aria-selected', on ? 'true' : 'false');
          tab.setAttribute('tabindex', on ? '0' : '-1');

          var fill = tab.querySelector('.mh-offers__progress-fill');
          if (!fill) return;

          // Restart the progress animation from zero on every activation.
          fill.style.animation = 'none';
          void fill.offsetWidth;
          fill.style.animation =
            on && autoplay ? 'mh-offer-progress ' + interval + 'ms linear forwards' : '';
        });

        panels.forEach(function (panel, i) {
          var on = i === index;
          panel.classList.toggle('is-active', on);
          if (on) {
            panel.removeAttribute('aria-hidden');
            panel.removeAttribute('tabindex');
          } else {
            panel.setAttribute('aria-hidden', 'true');
            panel.setAttribute('tabindex', '-1');
          }
        });

        current = index;
      }

      function stop() {
        if (timer) {
          clearTimeout(timer);
          timer = null;
        }
      }

      function schedule() {
        stop();
        if (!autoplay || held || !inView) return;
        timer = setTimeout(function () {
          paint((current + 1) % tabs.length);
          schedule();
        }, interval);
      }

      function select(index, focus) {
        paint(index);
        if (focus) tabs[index].focus();
        schedule();
      }

      tabs.forEach(function (tab, i) {
        tab.addEventListener('click', function () {
          select(i, false);
        });

        tab.addEventListener('keydown', function (e) {
          var next = null;
          if (e.key === 'ArrowDown' || e.key === 'ArrowRight') next = (i + 1) % tabs.length;
          if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') next = (i - 1 + tabs.length) % tabs.length;
          if (e.key === 'Home') next = 0;
          if (e.key === 'End') next = tabs.length - 1;
          if (next === null) return;
          e.preventDefault();
          select(next, true);
        });
      });

      // Pause while the visitor is reading or tabbing through it.
      function hold() {
        held = true;
        stop();
        var fill = tabs[current].querySelector('.mh-offers__progress-fill');
        if (fill) fill.style.animationPlayState = 'paused';
      }

      function release() {
        held = false;
        var fill = tabs[current].querySelector('.mh-offers__progress-fill');
        if (fill) fill.style.animationPlayState = 'running';
        schedule();
      }

      root.addEventListener('mouseenter', hold);
      root.addEventListener('mouseleave', release);
      root.addEventListener('focusin', hold);
      root.addEventListener('focusout', function (e) {
        if (!root.contains(e.relatedTarget)) release();
      });

      // Don't burn cycles (or skip offers) while the section is off screen.
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(
          function (entries) {
            inView = entries[0].isIntersecting;
            if (inView) {
              schedule();
            } else {
              stop();
            }
          },
          { threshold: 0.2 }
        ).observe(root);
      }

      paint(0);
      schedule();
    });
  }


  /* ------------------------------------------------------------- carousels */

  function initCarousels(scope) {
    var roots = scope.querySelectorAll('[data-mh-carousel]:not([data-mh-ready])');

    Array.prototype.forEach.call(roots, function (root) {
      root.setAttribute('data-mh-ready', 'true');

      var track = root.querySelector('[data-mh-track]');
      var thumb = root.querySelector('[data-mh-thumb]');
      var prev = root.querySelector('[data-mh-prev]');
      var next = root.querySelector('[data-mh-next]');
      if (!track) return;

      var ticking = false;

      function update() {
        var max = track.scrollWidth - track.clientWidth;

        if (thumb) {
          if (max <= 1) {
            thumb.style.width = '100%';
            thumb.style.transform = 'translateX(0)';
          } else {
            var ratio = track.clientWidth / track.scrollWidth;
            var barWidth = root.querySelector('[data-mh-bar]').clientWidth;
            thumb.style.width = Math.max(ratio * 100, 12) + '%';
            thumb.style.transform =
              'translateX(' + (track.scrollLeft / max) * (barWidth - thumb.offsetWidth) + 'px)';
          }
        }

        // Nothing to scroll - fewer cards than fit the row. Let the section
        // hide its bar and arrows rather than showing dead controls.
        root.classList.toggle('mh-is-static', max <= 1);

        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max - 2;
      }

      function onScroll() {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          update();
          ticking = false;
        });
      }

      function page(direction) {
        // Move by whole cards so the snap points stay aligned.
        var card = track.firstElementChild;
        var step = card ? card.offsetWidth : track.clientWidth;
        var perView = Math.max(1, Math.round(track.clientWidth / step));
        track.scrollBy({ left: direction * step * perView, behavior: reduced ? 'auto' : 'smooth' });
      }

      if (prev) prev.addEventListener('click', function () { page(-1); });
      if (next) next.addEventListener('click', function () { page(1); });

      track.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);

      /* ----------------------------------------------------------- autoplay */

      var autoplay = root.getAttribute('data-autoplay') === 'true' && !reduced;
      var interval = parseInt(root.getAttribute('data-interval') || '5000', 10);
      var timer = null;
      var inView = true;
      var held = false;

      function stop() {
        if (timer) {
          clearInterval(timer);
          timer = null;
        }
      }

      var busy = false;

      function tick() {
        var max = track.scrollWidth - track.clientWidth;
        if (max <= 1 || busy) return;

        // One step at a time: a smooth scroll that is still settling must not
        // have the next tick fire into it, or the two fight each other.
        busy = true;
        setTimeout(function () { busy = false; }, Math.min(900, interval - 100));

        // Wrap back to the start once the last card is showing.
        if (track.scrollLeft >= max - 2) {
          track.scrollTo({ left: 0, behavior: reduced ? 'auto' : 'smooth' });
        } else {
          page(1);
        }
      }

      function schedule() {
        stop();
        if (!autoplay || held || !inView) return;
        timer = setInterval(tick, interval);
      }

      if (autoplay) {
        root.addEventListener('mouseenter', function () { held = true; stop(); });
        root.addEventListener('mouseleave', function () { held = false; schedule(); });
        root.addEventListener('focusin', function () { held = true; stop(); });
        root.addEventListener('focusout', function (e) {
          if (!root.contains(e.relatedTarget)) { held = false; schedule(); }
        });
        // A deliberate swipe should not fight the timer.
        track.addEventListener('touchstart', function () { held = true; stop(); }, { passive: true });
        track.addEventListener('touchend', function () {
          held = false;
          setTimeout(schedule, 1200);
        }, { passive: true });

        if ('IntersectionObserver' in window) {
          new IntersectionObserver(
            function (entries) {
              inView = entries[0].isIntersecting;
              if (inView) { schedule(); } else { stop(); }
            },
            { threshold: 0.2 }
          ).observe(root);
        }
      }

      // Panels start hidden, so width is 0 until their tab is shown.
      root.__mhUpdate = update;
      root.__mhSchedule = schedule;
      update();
      schedule();
    });
  }

  /* ------------------------------------------------------------------ tabs */

  function initTabGroups(scope) {
    var groups = scope.querySelectorAll('[data-mh-tabs]:not([data-mh-ready])');

    Array.prototype.forEach.call(groups, function (group) {
      group.setAttribute('data-mh-ready', 'true');

      var tabs = Array.prototype.slice.call(group.querySelectorAll('[data-mh-tab]'));
      var panels = Array.prototype.slice.call(group.querySelectorAll('[data-mh-panel]'));
      if (!tabs.length) return;

      function select(index, focus) {
        tabs.forEach(function (tab, i) {
          tab.setAttribute('aria-selected', i === index ? 'true' : 'false');
          tab.setAttribute('tabindex', i === index ? '0' : '-1');
        });

        panels.forEach(function (panel, i) {
          if (i === index) {
            panel.removeAttribute('hidden');
          } else {
            panel.setAttribute('hidden', '');
          }
        });

        // The shown panel had no layout while hidden - resize its carousel now.
        var shown = panels[index];
        if (shown) {
          window.requestAnimationFrame(function () {
            var car = shown.querySelector('[data-mh-carousel]');
            if (car && car.__mhUpdate) car.__mhUpdate();
            sweep();
          });
        }

        if (focus) tabs[index].focus();
      }

      tabs.forEach(function (tab, i) {
        tab.addEventListener('click', function () { select(i, false); });

        tab.addEventListener('keydown', function (e) {
          var target = null;
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') target = (i + 1) % tabs.length;
          if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') target = (i - 1 + tabs.length) % tabs.length;
          if (e.key === 'Home') target = 0;
          if (e.key === 'End') target = tabs.length - 1;
          if (target === null) return;
          e.preventDefault();
          select(target, true);
        });
      });
    });
  }


  /* --------------------------------------------- cart actions + quick view */

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function shopUrl(path) {
    return ROOT.replace(/\/$/, '') + path;
  }

  function money(cents) {
    if (window.theme && theme.Currency && theme.Currency.formatMoney) {
      return theme.Currency.formatMoney(cents, theme.moneyFormat || '${{amount}}');
    }
    return (cents / 100).toFixed(2);
  }

  function cleanVendor(v) {
    return (v || '').replace(/^OUTLET\s*-\s*/i, '');
  }

  // The theme's cart drawer opens on a bubbling `ajaxProduct:added`, and other
  // listeners read [name="id"] off event.target - so dispatch from a real form,
  // exactly as the theme's own AjaxProduct does.
  var relayForm = null;
  function relay() {
    if (relayForm) return relayForm;
    relayForm = document.createElement('form');
    relayForm.setAttribute('data-mh-relay', '');
    relayForm.style.display = 'none';
    relayForm.innerHTML =
      '<input type="hidden" name="id" value=""><button type="button" class="add-to-cart"></button>';
    document.body.appendChild(relayForm);
    return relayForm;
  }

  function addToCart(variantId, quantity, button) {
    return fetch(shopUrl('/cart/add.js'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ id: Number(variantId), quantity: quantity || 1 })
    })
      .then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok) throw data;
          return data;
        });
      })
      .then(function (item) {
        var form = relay();
        form.querySelector('[name="id"]').value = variantId;
        form.dispatchEvent(
          new CustomEvent('ajaxProduct:added', {
            detail: { product: item, addToCartBtn: button || form.querySelector('.add-to-cart') },
            bubbles: true
          })
        );
        return item;
      });
  }

  /* ------------------------------------------------------- quick view panel */

  var qv = null;
  var qvProduct = null;
  var qvReturnFocus = null;

  function setMessage(text) {
    if (qv) qv.querySelector('.mh-qv__msg').textContent = text || '';
  }

  function currentVariant() {
    if (!qvProduct) return null;
    var selects = qv.querySelectorAll('[data-mh-opt]');
    if (!selects.length) return qvProduct.variants[0];

    var chosen = Array.prototype.map.call(selects, function (sel) {
      return sel.value;
    });

    for (var i = 0; i < qvProduct.variants.length; i++) {
      var v = qvProduct.variants[i];
      var match = true;
      for (var j = 0; j < chosen.length; j++) {
        if (v.options[j] !== chosen[j]) {
          match = false;
          break;
        }
      }
      if (match) return v;
    }
    return null;
  }

  function paintVariant() {
    var variant = currentVariant();
    var addBtn = qv.querySelector('.mh-qv__add');
    var label = addBtn.querySelector('.mh-cta__label');
    var priceEl = qv.querySelector('.mh-qv__price');
    var soldOut = soldOutLabel();
    var unavailable = (window.theme && theme.strings && theme.strings.unavailable) || 'Unavailable';

    setMessage('');

    if (!variant) {
      addBtn.disabled = true;
      label.textContent = unavailable;
      return;
    }

    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      priceEl.innerHTML =
        '<span class="mh-qv__now">' + money(variant.price) + '</span>' +
        '<s class="mh-qv__was">' + money(variant.compare_at_price) + '</s>';
    } else {
      priceEl.innerHTML = '<span class="mh-qv__now">' + money(variant.price) + '</span>';
    }

    if (variant.featured_image && variant.featured_image.src) {
      qv.querySelector('.mh-qv__img').src = variant.featured_image.src;
    }

    addBtn.disabled = !variant.available;
    label.textContent = variant.available ? 'Add to bag' : soldOut;
  }

  function buildQuickView() {
    if (qv) return qv;

    qv = document.createElement('div');
    qv.className = 'mh-qv';
    qv.setAttribute('hidden', '');
    qv.innerHTML =
      '<div class="mh-qv__overlay" data-mh-qv-close></div>' +
      '<div class="mh-qv__panel" role="dialog" aria-modal="true" aria-label="Quick view">' +
        '<button type="button" class="mh-qv__close" data-mh-qv-close aria-label="Close">' +
          '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="m2 2 12 12M14 2 2 14" stroke="currentColor" stroke-width="1.2"/></svg>' +
        '</button>' +
        '<div class="mh-qv__body">' +
          '<div class="mh-qv__media"><img class="mh-qv__img" alt=""></div>' +
          '<div class="mh-qv__info">' +
            '<p class="mh-qv__vendor"></p>' +
            '<h3 class="mh-qv__title"></h3>' +
            '<p class="mh-qv__price"></p>' +
            '<div class="mh-qv__options"></div>' +
            '<button type="button" class="mh-cta mh-cta--solid mh-qv__add">' +
              '<span class="mh-cta__label">Add to bag</span>' +
            '</button>' +
            '<p class="mh-qv__msg" role="status"></p>' +
            '<a class="mh-link mh-qv__full" href="#"><span>View full details</span></a>' +
          '</div>' +
        '</div>' +
      '</div>';

    document.body.appendChild(qv);

    qv.addEventListener('click', function (e) {
      if (e.target.closest('[data-mh-qv-close]')) closeQuickView();
    });

    qv.querySelector('.mh-qv__add').addEventListener('click', function () {
      var variant = currentVariant();
      var button = this;
      if (!variant || !variant.available) return;

      button.classList.add('is-busy');
      addToCart(variant.id, 1, button)
        .then(function () {
          button.classList.remove('is-busy');
          closeQuickView();
        })
        .catch(function (err) {
          button.classList.remove('is-busy');
          setMessage((err && err.description) || 'Sorry, that could not be added.');
        });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && qv && !qv.hasAttribute('hidden')) closeQuickView();
    });

    return qv;
  }

  function soldOutLabel() {
    return (window.theme && theme.strings && theme.strings.soldOut) || 'Sold out';
  }

  // Grey out the option values that have no available variant, given whatever
  // is currently chosen for the other options.
  function refreshOptionAvailability() {
    if (!qvProduct) return;
    var selects = Array.prototype.slice.call(qv.querySelectorAll('[data-mh-opt]'));
    if (!selects.length) return;

    var chosen = selects.map(function (sel) {
      return sel.value;
    });

    selects.forEach(function (sel, index) {
      Array.prototype.forEach.call(sel.options, function (opt) {
        var possible = qvProduct.variants.some(function (v) {
          if (!v.available) return false;
          if (v.options[index] !== opt.getAttribute('data-value')) return false;
          for (var j = 0; j < chosen.length; j++) {
            if (j !== index && v.options[j] !== chosen[j]) return false;
          }
          return true;
        });

        var base = opt.getAttribute('data-value');
        opt.disabled = !possible;
        opt.textContent = possible ? base : base + ' - ' + soldOutLabel();
      });
    });
  }

  // When a shopper changes one option, move the others to the nearest
  // combination that is actually in stock rather than leaving a dead end.
  function reconcileOptions(changedIndex) {
    var selects = Array.prototype.slice.call(qv.querySelectorAll('[data-mh-opt]'));
    if (!selects.length) return;

    var chosen = selects.map(function (sel) {
      return sel.value;
    });

    var exact = qvProduct.variants.filter(function (v) {
      return v.available && v.options.every(function (o, i) {
        return o === chosen[i];
      });
    });
    if (exact.length) return;

    var fallback = qvProduct.variants.find(function (v) {
      return v.available && v.options[changedIndex] === chosen[changedIndex];
    });
    if (!fallback) return;

    selects.forEach(function (sel, i) {
      if (i !== changedIndex) sel.value = fallback.options[i];
    });
  }

  function renderQuickView(product, url) {
    qvProduct = product;

    qv.querySelector('.mh-qv__vendor').textContent = cleanVendor(product.vendor);
    qv.querySelector('.mh-qv__title').textContent = product.title;
    qv.querySelector('.mh-qv__full').setAttribute('href', url || shopUrl('/products/' + product.handle));

    var img = qv.querySelector('.mh-qv__img');
    var src = product.featured_image || (product.images && product.images[0]) || '';
    img.src = typeof src === 'string' ? src : src.src;
    img.alt = product.title;

    // /products/x.js returns options either as plain strings or as objects.
    var optionNames = (product.options || []).map(function (o) {
      return typeof o === 'string' ? o : o.name;
    });

    // Open on something buyable, not just the first combination.
    var preferred = null;
    for (var i = 0; i < product.variants.length; i++) {
      if (product.variants[i].available) {
        preferred = product.variants[i];
        break;
      }
    }
    if (!preferred) preferred = product.variants[0];

    var optionsEl = qv.querySelector('.mh-qv__options');
    optionsEl.innerHTML = '';

    var hasRealOptions =
      product.variants.length > 1 || (optionNames.length && optionNames[0] !== 'Title');

    if (hasRealOptions) {
      optionNames.forEach(function (name, index) {
        var values = [];
        product.variants.forEach(function (v) {
          if (values.indexOf(v.options[index]) === -1) values.push(v.options[index]);
        });

        // A single-value option (e.g. Gender) is noise - skip the control but
        // keep a hidden input so variant matching still lines up by position.
        var field = document.createElement('label');
        field.className = 'mh-qv__field';
        if (values.length < 2) field.classList.add('mh-qv__field--fixed');
        field.innerHTML =
          '<span class="mh-qv__field-label"></span>' +
          '<select class="mh-qv__select" data-mh-opt></select>';
        field.querySelector('.mh-qv__field-label').textContent = name;

        var select = field.querySelector('select');
        values.forEach(function (value) {
          var opt = document.createElement('option');
          opt.value = value;
          opt.setAttribute('data-value', value);
          opt.textContent = value;
          select.appendChild(opt);
        });

        if (preferred) select.value = preferred.options[index];

        select.addEventListener('change', function () {
          reconcileOptions(index);
          refreshOptionAvailability();
          paintVariant();
        });

        optionsEl.appendChild(field);
      });
    }

    refreshOptionAvailability();
    qv.querySelector('.mh-qv__body').classList.remove('is-loading');
    paintVariant();
  }

  function openQuickView(card) {
    if (!card) return;
    var handle = card.getAttribute('data-handle');
    if (!handle) return;

    qvReturnFocus = document.activeElement;
    buildQuickView();

    qv.removeAttribute('hidden');
    document.documentElement.classList.add('mh-qv-open');
    qv.querySelector('.mh-qv__body').classList.add('is-loading');
    qv.querySelector('.mh-qv__title').textContent = card.getAttribute('data-title') || '';
    qv.querySelector('.mh-qv__options').innerHTML = '';
    qv.querySelector('.mh-qv__price').innerHTML = '';
    setMessage('');

    window.requestAnimationFrame(function () {
      qv.classList.add('is-open');
      qv.querySelector('.mh-qv__close').focus();
    });

    fetch(shopUrl('/products/' + handle + '.js'), { credentials: 'same-origin' })
      .then(function (r) {
        return r.json();
      })
      .then(function (product) {
        renderQuickView(product, card.getAttribute('data-url'));
      })
      .catch(function () {
        qv.querySelector('.mh-qv__body').classList.remove('is-loading');
        setMessage('Sorry, this product could not be loaded.');
      });
  }

  function closeQuickView() {
    if (!qv) return;
    qv.classList.remove('is-open');
    document.documentElement.classList.remove('mh-qv-open');
    setTimeout(function () {
      qv.setAttribute('hidden', '');
    }, 350);
    if (qvReturnFocus && qvReturnFocus.focus) qvReturnFocus.focus();
  }

  /* ------------------------------------------------------- card delegation */

  function handleAddClick(button) {
    var card = button.closest('[data-mh-product]');
    if (!card || button.disabled) return;

    // Several variants means a size or colour has to be chosen first.
    if (parseInt(card.getAttribute('data-variant-count') || '1', 10) > 1) {
      openQuickView(card);
      return;
    }

    var id = card.getAttribute('data-variant-id');
    if (!id) {
      openQuickView(card);
      return;
    }

    button.classList.add('is-busy');
    addToCart(id, 1, button)
      .then(function () {
        button.classList.remove('is-busy');
        button.classList.add('is-done');
        setTimeout(function () {
          button.classList.remove('is-done');
        }, 2000);
      })
      .catch(function () {
        button.classList.remove('is-busy');
        openQuickView(card);
      });
  }

  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;

    var add = e.target.closest('[data-mh-atc]');
    if (add) {
      e.preventDefault();
      handleAddClick(add);
      return;
    }

    var quick = e.target.closest('[data-mh-quick]');
    if (quick) {
      e.preventDefault();
      openQuickView(quick.closest('[data-mh-product]'));
    }
  });


  /* ------------------------------------------------------------- slideshows */

  function initSlideshows(scope) {
    var roots = scope.querySelectorAll('[data-mh-slides]:not([data-mh-ready])');

    Array.prototype.forEach.call(roots, function (root) {
      root.setAttribute('data-mh-ready', 'true');

      var slides = Array.prototype.slice.call(root.querySelectorAll('[data-mh-slide]'));
      var dots = Array.prototype.slice.call(root.querySelectorAll('[data-mh-slide-dot]'));
      var prev = root.querySelector('[data-mh-slide-prev]');
      var next = root.querySelector('[data-mh-slide-next]');
      if (slides.length < 2) return;

      var autoplay = root.getAttribute('data-autoplay') === 'true' && !reduced;
      var interval = parseInt(root.getAttribute('data-interval') || '5000', 10);
      var current = 0;
      var timer = null;
      var inView = true;
      var held = false;

      function paint(index) {
        slides.forEach(function (slide, i) {
          var on = i === index;
          slide.classList.toggle('is-active', on);
          if (on) {
            slide.removeAttribute('aria-hidden');
          } else {
            slide.setAttribute('aria-hidden', 'true');
          }
        });

        dots.forEach(function (dot, i) {
          dot.setAttribute('aria-selected', i === index ? 'true' : 'false');
        });

        current = index;
      }

      function stop() {
        if (timer) {
          clearTimeout(timer);
          timer = null;
        }
      }

      function schedule() {
        stop();
        if (!autoplay || held || !inView) return;
        timer = setTimeout(function () {
          paint((current + 1) % slides.length);
          schedule();
        }, interval);
      }

      function go(index, focus) {
        paint((index + slides.length) % slides.length);
        if (focus && dots[current]) dots[current].focus();
        schedule();
      }

      dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () {
          go(i, false);
        });

        dot.addEventListener('keydown', function (e) {
          var target = null;
          if (e.key === 'ArrowRight') target = i + 1;
          if (e.key === 'ArrowLeft') target = i - 1;
          if (e.key === 'Home') target = 0;
          if (e.key === 'End') target = slides.length - 1;
          if (target === null) return;
          e.preventDefault();
          go(target, true);
        });
      });

      if (prev) prev.addEventListener('click', function () { go(current - 1, false); });
      if (next) next.addEventListener('click', function () { go(current + 1, false); });

      function hold() {
        held = true;
        stop();
      }

      function release() {
        held = false;
        schedule();
      }

      root.addEventListener('mouseenter', hold);
      root.addEventListener('mouseleave', release);
      root.addEventListener('focusin', hold);
      root.addEventListener('focusout', function (e) {
        if (!root.contains(e.relatedTarget)) release();
      });

      // Swipe, without hijacking a vertical page scroll.
      var startX = null;
      var startY = null;
      root.addEventListener(
        'touchstart',
        function (e) {
          startX = e.touches[0].clientX;
          startY = e.touches[0].clientY;
        },
        { passive: true }
      );
      root.addEventListener(
        'touchend',
        function (e) {
          if (startX === null) return;
          var dx = e.changedTouches[0].clientX - startX;
          var dy = e.changedTouches[0].clientY - startY;
          if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) {
            go(dx < 0 ? current + 1 : current - 1, false);
          }
          startX = null;
          startY = null;
        },
        { passive: true }
      );

      // Don't rotate (or burn a slide) while the banner is off screen.
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(
          function (entries) {
            inView = entries[0].isIntersecting;
            if (inView) {
              schedule();
            } else {
              stop();
            }
          },
          { threshold: 0.15 }
        ).observe(root);
      }

      paint(0);
      schedule();
    });
  }

  /* ------------------------------------------------------------------ boot */

  /* ------------------------------------------------------- equal widths */

  // Give every element matching the group's selector the width of the widest
  // one, so a row of links lines up even when one label is longer.
  function initEqualise(scope) {
    var groups = scope.querySelectorAll('[data-mh-equalise]');

    Array.prototype.forEach.call(groups, function (group) {
      var selector = group.getAttribute('data-mh-equalise');
      if (!selector) return;

      function run() {
        var items = group.querySelectorAll(selector);
        if (items.length < 2) return;

        // Release first: a previous pass may already be holding them open, and
        // measuring that would ratchet the width up and never come back down.
        Array.prototype.forEach.call(items, function (el) {
          el.style.width = '';
        });

        var widest = 0;
        Array.prototype.forEach.call(items, function (el) {
          var w = el.getBoundingClientRect().width;
          if (w > widest) widest = w;
        });

        if (!widest) return;
        Array.prototype.forEach.call(items, function (el) {
          el.style.width = Math.ceil(widest) + 'px';
        });
      }

      run();

      // Re-measure once the webfont is in; until then every width is the
      // fallback face's and they all come out wrong together.
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(run).catch(function () {});
      }

      var pending = null;
      window.addEventListener('resize', function () {
        clearTimeout(pending);
        pending = setTimeout(run, 150);
      });
    });
  }

  function init(scope) {
    var root = scope || document;
    initReveal(root);
    initOffers(root);
    initCarousels(root);
    initTabGroups(root);
    initSlideshows(root);
    initEqualise(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  // Shopify theme editor: sections are re-rendered without a page load.
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
})();
